/**
 * The shared field-validation error registry (ADR-20260918-htmd).
 *
 * Every entry is a distinct, Content-Design-authored string, keyed by field
 * and situation (`specs/features/m1-portal-routing-resume-model/screens/error-registry.md`
 * is this table's content spec). This module is a LOOKUP device only — it
 * never builds a message from a shared template at runtime (no `${}`
 * interpolation, no "invalid ${field}" generator). Adding a new field/
 * situation means adding a new, separately-authored string here, not
 * parameterising an existing one.
 *
 * AC-10: a required field left empty at the point the customer tries to
 * proceed is named individually on screen, never indicated by colour alone.
 * Callers render the string this module returns as visible text next to the
 * field — colour, where also used, is never the only signal (WCAG 1.4.1).
 */
export const ERROR_REGISTRY = {
  // TG-6 (AC-20): byte-identical to the Gherkin scenario's exact wording —
  // the H1-era strings these two entries used to carry ("Enter the
  // date.", a trailing period, "I agree to this statement") are retired;
  // screen 6 is H2 now and these are the two messages AC-20 actually
  // names. "declaration.date" is unused from this task group onward
  // (screen 6 shows today's date as static text, never a field a customer
  // can leave blank) but the entry is kept, not deleted, in case a future
  // change reintroduces an editable date.
  // REQ-004 (PR 3814): one message per server `reason` for this field.
  // `missing` is for a blank name only. No full stop, matching screen 6's
  // other two messages. `invalidCharacters` covers the non-printing
  // characters the server refuses (a name usually picks them up by being
  // pasted), so it leads with the fix and states the cause without blame.
  "declaration.representativeName": {
    missing: "Enter the name of the authorized representative",
    tooLong: "Enter no more than 200 characters",
    invalidCharacters: "Type the name again instead of pasting it, because it includes hidden characters we cannot accept",
  },
  "declaration.date": {
    missing: "Enter the date.",
  },
  "declaration.acknowledgement": {
    missing: "Agree to the declaration statement",
  },
  "exposure.freeText": {
    empty: "You have not answered this question.",
    tooLong: "Enter no more than 500 characters.",
  },
  "changeCheck.hasAnythingChanged": {
    missing: "Select 'Yes' or 'No' to tell us whether anything has changed.",
  },
  // TG-6 (AC-16): byte-identical to the product-spec's own Gherkin wording
  // for the Assign dialog.
  "assign.noSectionTicked": {
    missing: "Select at least one section to assign to a colleague",
  },
  "assign.assigneeName": {
    missing: "Enter their name",
  },
  "assign.assigneeEmail": {
    missing: "Enter their email address",
    // Distinct wording from "confirmDetails.contactEmail" and sign-in's
    // own format message (both already registered) — the registry's own
    // rule is one hand-authored string per entry, never a shared template
    // reused verbatim across screens.
    // H2 04-05-GuidedExposures: the error SUMMARY line for a wrong-shape
    // email. The field-level line H2 shows beside the input is the longer
    // "…, like name@example.com" message, which is already registered as
    // "confirmDetails.contactEmail".invalidFormat; the dialogs read that
    // entry rather than registering the same string twice (this
    // registry's own distinct-wording rule).
    invalidFormat: "Enter an email address in the correct format",
  },
  // TG-6 (AC-17): the same-person refusal shown by both the Assign and
  // Reassign dialogs.
  "assign.samePerson": {
    conflict: "This section is already assigned to this person. Enter a different name or email address",
  },
  // H2 04-05-GuidedExposures: the Reassign dialog's Confirm with neither
  // option chosen.
  "reassign.choice": {
    missing: "Select what you would like to do",
  },
  // TG-5/AC-12: byte-identical to H2's own stated validation messages
  // (design_handoff_renewals_portal_H2/README.md "Screen 3 — Confirm your
  // details"). The email format message is distinct from screen 1's own
  // "like name@company.com" (AC-5/AC-21) -- the two are separately
  // authored, same-shape messages for two different screens, never one
  // shared template.
  "confirmDetails.businessName": {
    missing: "Enter your business name",
  },
  "confirmDetails.mailingAddress": {
    missing: "Enter your mailing address",
  },
  "confirmDetails.contactName": {
    missing: "Enter a primary contact name",
  },
  "confirmDetails.contactPhone": {
    missing: "Enter a primary phone number",
  },
  "confirmDetails.contactEmail": {
    missing: "Enter a primary email address",
    invalidFormat: "Enter an email address in the correct format, like name@example.com",
  },
} as const;

export type ErrorRegistryField = keyof typeof ERROR_REGISTRY;

export type ErrorRegistrySituation<Field extends ErrorRegistryField> = keyof (typeof ERROR_REGISTRY)[Field];

/**
 * Looks up the Content-Design-authored string for a given field and
 * situation. The two-parameter signature (rather than a single combined
 * key) is what makes this a lookup device rather than a template: there is
 * no way to call this with a field/situation pair that is not itself a
 * distinct, hand-authored registry entry.
 */
export function getErrorMessage<Field extends ErrorRegistryField>(
  field: Field,
  situation: ErrorRegistrySituation<Field>,
): string {
  const situations = ERROR_REGISTRY[field] as Record<string, string>;
  return situations[situation as string]!;
}

/**
 * The stated character limit for an exposure free-text field (TG-7
 * acceptance criteria — "a stated character limit"). Not a magic number
 * repeated at each call site: every caller reads it from here.
 */
export const EXPOSURE_FREE_TEXT_MAX_LENGTH = 500;

/** THR-4's ceiling for the declaration's representative name (`src/api/functions/submission.py`,
 * `_REPRESENTATIVE_NAME_MAX_LENGTH`). Screen 6 validates against it on submit; the input carries no
 * `maxlength`, which would silently truncate a pasted name. */
export const DECLARATION_REPRESENTATIVE_NAME_MAX_LENGTH = 200;

/**
 * Validates one exposure free-text answer against the two gaps the design
 * handoff's own source material flagged as unreinstated: an empty answer,
 * and an answer beyond the stated character limit. A whitespace-only
 * answer counts as empty — it is not a real answer to the question. Pure
 * function, no rendering: whichever component renders an exposure
 * free-text field (a later task group's concern) calls this and renders
 * the returned message as visible text next to the field, exactly as
 * DeclarationRoute.tsx does for its own fields.
 */
export function validateExposureFreeText(value: string): string | undefined {
  if (value.trim().length === 0) {
    return getErrorMessage("exposure.freeText", "empty");
  }
  // Count Unicode code points, not UTF-16 code units: `.length` counts a
  // surrogate pair (emoji, supplementary-plane CJK) as two, which would
  // reject a customer's answer that has exactly the stated number of
  // characters (OBJ-113 / FIND-161).
  if ([...value].length > EXPOSURE_FREE_TEXT_MAX_LENGTH) {
    return getErrorMessage("exposure.freeText", "tooLong");
  }
  return undefined;
}

/**
 * Validates the change-check section's own required radio answer
 * (AC-10/OBJ-52). `''` (unanswered) is the ambiguous default
 * `ADR-20260918-ft3j` names — never a legitimate stored value — so it is
 * rejected the same way a missing declaration field is: named individually
 * on screen, with no write attempted (`MultiSectionStepRoute.handleConfirm`).
 */
export function validateChangeCheckAnswer(value: string): string | undefined {
  if (value !== "yes" && value !== "no") {
    return getErrorMessage("changeCheck.hasAnythingChanged", "missing");
  }
  return undefined;
}

/**
 * AC-12's own non-regex email shape check, mirroring `routes/sign-in/
 * SignInPage.tsx`'s `isSyntacticallyValidEmail` (same reasoning: a single
 * combined pattern lets two of its own `+` groups both match ".", forcing
 * super-linear backtracking on a long run of dots with no "@" -- see that
 * file's own doc comment for the full argument). A syntax gate only, not
 * an exhaustive email grammar: one "@", a non-blank local part and domain
 * either side, and at least one "." strictly between the domain's first
 * and last characters.
 */
function isSyntacticallyValidEmail(value: string): boolean {
  const atIndex = value.indexOf("@");
  if (atIndex === -1 || value.includes("@", atIndex + 1)) {
    return false;
  }
  const local = value.slice(0, atIndex);
  const domain = value.slice(atIndex + 1);
  if (local.length === 0 || domain.length === 0) {
    return false;
  }
  if (/\s/.test(local) || /\s/.test(domain)) {
    return false;
  }
  return domain.slice(1, -1).includes(".");
}

const CONFIRM_DETAILS_FIELD_IDS = [
  "businessName",
  "mailingAddress",
  "contactName",
  "contactPhone",
  "contactEmail",
] as const;

export type ConfirmDetailsFieldId = (typeof CONFIRM_DETAILS_FIELD_IDS)[number];

export function isConfirmDetailsFieldId(fieldId: string): fieldId is ConfirmDetailsFieldId {
  return (CONFIRM_DETAILS_FIELD_IDS as readonly string[]).includes(fieldId);
}

/**
 * AC-12: a blank field shows its own H2 message; `contactEmail` additionally
 * validates shape. No rule for the other four fields beyond "not blank" —
 * H2 states none, and the editable-fields Business Rule ("any field a
 * customer can edit in the prototype is editable... with no Account
 * Manager review step") argues against inventing one.
 */
const CONFIRM_DETAILS_MISSING_MESSAGE: Record<ConfirmDetailsFieldId, string> = {
  businessName: ERROR_REGISTRY["confirmDetails.businessName"].missing,
  mailingAddress: ERROR_REGISTRY["confirmDetails.mailingAddress"].missing,
  contactName: ERROR_REGISTRY["confirmDetails.contactName"].missing,
  contactPhone: ERROR_REGISTRY["confirmDetails.contactPhone"].missing,
  contactEmail: ERROR_REGISTRY["confirmDetails.contactEmail"].missing,
};

export function validateConfirmDetailsField(fieldId: ConfirmDetailsFieldId, value: string): string | undefined {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return CONFIRM_DETAILS_MISSING_MESSAGE[fieldId];
  }
  if (fieldId === "contactEmail" && !isSyntacticallyValidEmail(trimmed)) {
    return getErrorMessage("confirmDetails.contactEmail", "invalidFormat");
  }
  return undefined;
}

/**
 * AC-12: "the phone field keeps only digits, spaces and the characters
 * ( ) + - . as the customer types" — a single-pass character-class strip,
 * applied to every keystroke's resulting value (never a regex with
 * repetition over an unbounded alternation, so this carries no ReDoS
 * shape either).
 */
export function sanitizePhoneInput(value: string): string {
  return value.replace(/[^\d\s()+\-.]/g, "");
}
